import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { EVENT_NAMES, type FavoriteAddedDetail } from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';

const AUTO_CLOSE_MS = 3000;

/** kd-favorite-added イベントを受信してトーストを表示する。HTML は v2-favorite-toast.liquid が生成し、振る舞いのみ担当 */
@customElement('kd-favorite-toast')
export class KdFavoriteToast extends LitElement {
  createRenderRoot() {
    return this;
  }

  render() {
    return nothing;
  }

  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * 見出しの既定文言。Liquid（v2-favorite-toast.liquid）の初期 textContent を SSoT として退避する。
   * カート「あとで買う」等で detail.toastLabel が来たときは差し替え、来ないときは必ずこれへ戻す
   * （単一共有トーストのため文言リークを防ぐ）。
   */
  private defaultHeading = '';

  connectedCallback(): void {
    super.connectedCallback();
    const headingEl = this.querySelector<HTMLElement>(SELECTORS.FAVORITE_TOAST.HEADING);
    this.defaultHeading = headingEl?.textContent?.trim() ?? '';
    document.addEventListener(EVENT_NAMES.FAVORITE.ADDED, this.handleFavoriteAdded);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener(EVENT_NAMES.FAVORITE.ADDED, this.handleFavoriteAdded);
    this.removeEventListener('click', this.handleClick);
    if (this.closeTimer !== null) clearTimeout(this.closeTimer);
  }

  private handleClick = (event: Event): void => {
    if ((event.target as HTMLElement).closest(SELECTORS.FAVORITE_TOAST.CLOSE)) {
      this.closeToast();
    }
  };

  private handleFavoriteAdded = (event: Event): void => {
    const detail = (event as CustomEvent<FavoriteAddedDetail>).detail;
    this.showToast(detail);
  };

  private showToast(detail: FavoriteAddedDetail): void {
    const panel = this.querySelector<HTMLElement>(SELECTORS.FAVORITE_TOAST.PANEL);
    if (!panel) return;

    const headingEl = this.querySelector<HTMLElement>(SELECTORS.FAVORITE_TOAST.HEADING);
    const imgEl = this.querySelector<HTMLImageElement>(SELECTORS.FAVORITE_TOAST.PRODUCT_IMAGE);
    const titleEl = this.querySelector<HTMLElement>(SELECTORS.FAVORITE_TOAST.PRODUCT_TITLE);

    if (this.closeTimer !== null) clearTimeout(this.closeTimer);

    // 見出しは常に設定する。上書き文言があれば差し替え、無ければ既定へ戻す（共有トーストの文言リーク防止）。
    if (headingEl) headingEl.textContent = detail.toastLabel || this.defaultHeading;

    if (imgEl) {
      if (detail.productImageUrl) {
        imgEl.src = detail.productImageUrl;
        imgEl.alt = detail.productTitle;
        imgEl.hidden = false;
      } else {
        imgEl.hidden = true;
      }
    }
    if (titleEl) titleEl.textContent = detail.productTitle;
    panel.hidden = false;
    this.closeTimer = setTimeout(this.closeToast, AUTO_CLOSE_MS);
  }

  private closeToast = (): void => {
    const panel = this.querySelector<HTMLElement>(SELECTORS.FAVORITE_TOAST.PANEL);
    if (panel) panel.hidden = true;
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-favorite-toast': KdFavoriteToast;
  }
}
